import type {
  Translator,
  VisitorFieldError,
  VisitorInput,
} from './visitorIntegrationValidation';

/**
 * Kastle rejects visitor names containing anything outside its allowed set
 * (StatusCode 6: "The Use of Special Characters except space, comma, '.', '&',
 * ''', '_', '-', '()' are Not Allowed").
 *
 * Names are checked exactly as entered, with no normalization, so accented
 * letters (é) and curly apostrophes (’) are rejected along with everything
 * else outside the allowed set.
 */

const KASTLE_ALLOWED_CHARACTER = /^[A-Za-z0-9 ,.&'_\-()]$/;

// Format/control characters: zero-width space, BOM, soft hyphen, tabs, line breaks, …
const INVISIBLE_CHARACTER = /^[\p{Cf}\p{Cc}\p{Zl}\p{Zp}]$/u;
// Any space separator; U+0020 itself is allowed, so it never reaches classification.
const SPACE_SEPARATOR = /^\p{Zs}$/u;
// Full-width forms of ASCII punctuation and letters (U+FF01–U+FF5E), e.g. "\uFF08" and "\uFF09".
const FULL_WIDTH_CHARACTER = /^[\uFF01-\uFF5E]$/;

export type UnsupportedNameCharacterKind = 'hidden' | 'fullWidth' | 'other';

export const KASTLE_NAME_MESSAGE_KEYS: Record<
  UnsupportedNameCharacterKind,
  string
> = {
  hidden:
    'web.content.feature.visitorManagement.visitorInvite.form.unsupportedNameCharacter.hidden',
  fullWidth:
    'web.content.feature.visitorManagement.visitorInvite.form.unsupportedNameCharacter.fullWidth',
  other:
    'web.content.feature.visitorManagement.visitorInvite.form.unsupportedNameCharacter.other',
};

/** Returns the first character Kastle would reject, or null when the name is acceptable. */
export function findUnsupportedKastleCharacter(
  name: string | null | undefined
): string | null {
  if (!name) {
    return null;
  }

  for (const character of name) {
    if (!KASTLE_ALLOWED_CHARACTER.test(character)) {
      return character;
    }
  }

  return null;
}

export function classifyUnsupportedCharacter(
  character: string
): UnsupportedNameCharacterKind {
  if (INVISIBLE_CHARACTER.test(character) || SPACE_SEPARATOR.test(character)) {
    return 'hidden';
  }

  if (FULL_WIDTH_CHARACTER.test(character)) {
    return 'fullWidth';
  }

  return 'other';
}

/** "U+200B" — for logs and support; never shown on its own to users. */
export function toCodePoint(character: string): string {
  return `U+${character
    .codePointAt(0)!
    .toString(16)
    .toUpperCase()
    .padStart(4, '0')}`;
}

/** Why Kastle would reject this name, or null when it is acceptable. */
export function findKastleNameRejection(
  name: string | null | undefined,
  translator: Translator
): Omit<VisitorFieldError, 'field'> | null {
  const character = findUnsupportedKastleCharacter(name);

  if (character === null) {
    return null;
  }

  const kind = classifyUnsupportedCharacter(character);

  return {
    kind,
    character,
    message: translator(KASTLE_NAME_MESSAGE_KEYS[kind], { character }),
  };
}

/** Kastle's visitor rules; registered in visitorIntegrationValidation. */
export function validateInputForKastle(
  input: VisitorInput,
  translator: Translator
): VisitorFieldError[] {
  return (['firstName', 'lastName'] as const).flatMap(field => {
    const rejection = findKastleNameRejection(input[field], translator);

    return rejection ? [{ field, ...rejection }] : [];
  });
}
